import { Actions, ofType } from "@ngrx/effects";
import { ActiveMqChannelI, OrderAppChannelI } from "../../_config/channels/shared-channel.const";
import { Client, StompHeaders, StompSubscription, TickerStrategy } from '@stomp/stompjs';
import { Injectable, effect, inject } from "@angular/core";
import { SpxToasterAutoCloseSpeedEnum, spxToasterActions } from "@softpak/components/spx-toaster";
import { captureMessage, getCurrentScope } from "@sentry/angular";

import { AmqRtgSubscriptionConfigI } from "./models/amq-rtg-subscription-config.interface";
import { AmqSocketActions } from "src/app/store/amq-socket/amq-socket.actions";
import { AmqSubscriptionConfigI } from "./models/amq-subscription-config.interface";
import { AmqSubscriptionTypeEnum } from "./models/amq-subscription-type.enum";
import { RouterNavigationAction } from "@ngrx/router-store";
import { SharedStorageKeyEnum } from "../../_config/storage/shared-storage-key-enum";
import { SpxStorage } from "@softpak/components/spx-storage";
import { Store } from "@ngrx/store";
import { filter } from "rxjs";
import { sharedChannelList } from "./models/shared-activemq-channels";
import { sharedEnvironmentReducer } from "src/app/store/shared-environment-info";
import { sharedGetEquipmentReducer } from "src/app/store/shared-get-equipment";
import { spxChannelReducer } from "@softpak/components/spx-channel-selection";
import { valuePairToValue } from "@softpak/components/spx-helpers";

@Injectable({
  providedIn: 'root'
})
export class AmqService {
  private readonly appStore = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly signedIn = this.appStore.selectSignal(sharedEnvironmentReducer.default.selectLoaded);
  private readonly channel = this.appStore.selectSignal(spxChannelReducer.default.selectChannel);
  private readonly selectedEquipment = this.appStore.selectSignal(sharedGetEquipmentReducer.default.selectSelectedEquipment);
  private readonly selectedEquipmentIsRtg = this.appStore.selectSignal(sharedGetEquipmentReducer.default.selectSelectedEquipmentIsRtg);

  private activeMqClient: Client;
  private subscriptions: StompSubscription[] = [];
  private currentPage: string;
  private currentEquipment: string;
  private currentBrokerUrl: string;
  private heartbeatLost = false;
  private shouldShowReconnectSuccess = false;
  private duplicatedSubscriptionLogged = false;
  private readonly reconnectDelayMs = 3000;

  private _listenToChannelAndAuthState = effect(() => {
    this.ensureConnection('...');
  });

  constructor() {
    this.onNavigationChange();
  }

  onNavigationChange(): void {
    this.actions$
      .pipe(
        ofType('@ngrx/router-store/navigation'),
        filter((action: RouterNavigationAction<any>) => !!action.payload)
      )
      .subscribe((action) => {
        this.currentPage = action.payload.routerState.url;
      });
  }

  addSubscriptions(): void {
    this.addRtgSubscription();
  }

  private addRtgSubscription(): void {
    const selectedEquipment = this.selectedEquipment();
    if (!selectedEquipment) {
      return;
    }

    const rtgSubscription = sharedChannelList.find(i => i.type === AmqSubscriptionTypeEnum.equipmentSubscription) as AmqRtgSubscriptionConfigI;
    rtgSubscription.currentEquipment = selectedEquipment;
    rtgSubscription.currentDepot = valuePairToValue(SpxStorage.getChannelSetting(SharedStorageKeyEnum.depot));
    this.currentEquipment = selectedEquipment;
    this.log('addRtgSubscription:before-subscribe');
    this.addSubscription(this.activeMqClient, rtgSubscription);
  }

  private connect(activeMqChannel: ActiveMqChannelI, messageText: string): void {
    this.log('connect:requested');
    console.info(`[AMQ] ${messageText}`);
    this.appStore.dispatch(AmqSocketActions.log({
      logType: 'info',
      message: messageText,
    }));

    this.disconnect();
    this.currentBrokerUrl = activeMqChannel.url;
    this.activeMqClient = this.createClient(activeMqChannel);
    this.activeMqClient.activate();
  }

  private disconnect(): void {
    const shouldLogDisconnect = !!this.activeMqClient?.connected || this.subscriptions.length > 0;

    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];

    if (this.activeMqClient !== undefined) {
      this.activeMqClient.deactivate();
      this.activeMqClient = undefined;
    }

    if (shouldLogDisconnect) {
      this.appStore.dispatch(AmqSocketActions.log({
        logType: 'disconnect',
        message: 'ActiveMQ disconnected',
      }));
    }

    this.heartbeatLost = false;
    this.shouldShowReconnectSuccess = false;
    this.duplicatedSubscriptionLogged = false;
    this.currentBrokerUrl = undefined;
    this.currentEquipment = undefined;
  }

  private createClient(activeMqChannel: ActiveMqChannelI): Client {
    let hasConnected = false;
    let hasConnectionError = false;

    let stompHeaders: StompHeaders;

    if (activeMqChannel.user && activeMqChannel.password) {
      stompHeaders = {
        login: activeMqChannel.user,
        passcode: activeMqChannel.password
      };
    }

    const client = new Client({
      brokerURL: activeMqChannel.url,
      reconnectDelay: this.reconnectDelayMs,
      heartbeatIncoming: 20000,
      heartbeatOutgoing: 20000,
      heartbeatStrategy: TickerStrategy.Worker,
      connectHeaders: stompHeaders,
      onConnect: () => {
        if (this.activeMqClient !== client) {
          return;
        }

        hasConnected = true;
        this.log('onConnect:before-subscribe');
        this.addSubscriptions();
        this.log('onConnect:after-subscribe');

        const successMessage = this.shouldShowReconnectSuccess || this.heartbeatLost
          ? `ActiveMQ connection restored: ${this.currentEquipment}`
          : `ActiveMQ connected: ${this.currentEquipment}`;
        this.appStore.dispatch(spxToasterActions.createSuccess(
          this.shouldShowReconnectSuccess || this.heartbeatLost
            ? {
              messageText: successMessage,
            }
            : {
              autoClose: SpxToasterAutoCloseSpeedEnum.DEFAULT,
              messageText: successMessage,
            }
        ));
        this.appStore.dispatch(AmqSocketActions.log({
          logType: 'connect',
          message: successMessage,
        }));
        this.heartbeatLost = false;
        this.shouldShowReconnectSuccess = false;
      },
      onHeartbeatLost: () => {
        if (this.activeMqClient !== client || this.heartbeatLost) {
          return;
        }

        this.heartbeatLost = true;
        const warningMessage = `ActiveMQ connection seems lost. Waiting for heartbeat response...`;
        this.appStore.dispatch(spxToasterActions.createWarning({
          autoClose: SpxToasterAutoCloseSpeedEnum.DEFAULT,
          messageText: warningMessage,
        }));
        this.appStore.dispatch(AmqSocketActions.log({
          logType: 'error',
          message: warningMessage,
        }));
      },
      onHeartbeatReceived: () => {
        if (this.activeMqClient !== client || !this.heartbeatLost) {
          return;
        }

        this.heartbeatLost = false;
        const restoredMessage = `ActiveMQ heartbeat restored: ${this.currentEquipment}`;
        this.appStore.dispatch(spxToasterActions.createSuccess({
          messageText: restoredMessage,
        }));
        this.appStore.dispatch(AmqSocketActions.log({
          logType: 'connect',
          message: restoredMessage,
        }));
      },
      onStompError: (frame) => {
        if (this.activeMqClient !== client) {
          return;
        }

        hasConnectionError = true;
        const errorMessage = `ActiveMQ error: ${frame?.headers?.message ?? 'Unknown STOMP error'}`;
        this.appStore.dispatch(spxToasterActions.createError({
          messageText: errorMessage,
        }));
        this.appStore.dispatch(AmqSocketActions.log({
          logType: 'error',
          message: errorMessage,
        }));
      },
      onWebSocketError: () => {
        if (this.activeMqClient !== client) {
          return;
        }

        hasConnectionError = true;
        const errorMessage = `Error during connecting`;
        this.appStore.dispatch(spxToasterActions.createError({
          messageText: errorMessage,
        }));
        this.appStore.dispatch(AmqSocketActions.log({
          logType: 'error',
          message: errorMessage,
        }));
      },
      onWebSocketClose: () => {
        if (this.activeMqClient !== client) {
          return;
        }

        this.subscriptions = [];

        if (!hasConnected && !hasConnectionError) {
          const errorMessage = `Error connecting to ActiveMQ socket`;
          this.appStore.dispatch(spxToasterActions.createError({
            messageText: errorMessage,
          }));
          this.appStore.dispatch(AmqSocketActions.log({
            logType: 'error',
            message: errorMessage,
          }));
        } else if (hasConnected) {
          this.shouldShowReconnectSuccess = true;
          this.appStore.dispatch(AmqSocketActions.log({
            logType: 'disconnect',
            message: 'Disconnected',
          }));
        }
      }
    });

    return client;
  }

  private addSubscription(activeMqClient: Client, subscriptionType: AmqRtgSubscriptionConfigI): void {
    this.log('addSubscription:before-client-subscribe');
    this.subscriptions.push(activeMqClient.subscribe(
      subscriptionType.activeMqChannel,
      this.callback.bind(this, subscriptionType),
      { selector: `equipmentNumber='${subscriptionType.currentEquipment}'`, } as StompHeaders)
    );
    this.log('addSubscription:after-client-subscribe');

    this.reportDuplicateSubscriptions(subscriptionType.activeMqChannel);
  }

  private clearConnectionState(client?: Client): void {
    if (client && this.activeMqClient !== client) {
      return;
    }

    this.subscriptions = [];
    this.activeMqClient = undefined;
    this.currentBrokerUrl = undefined;
    this.currentEquipment = undefined;
  }

  private ensureConnection(reason: string): void {
    const channel = this.channel() as OrderAppChannelI;
    const selectedEquipment = this.selectedEquipment();

    if (!this.signedIn() || !channel?.activeMq || !selectedEquipment || !this.selectedEquipmentIsRtg()) {
      this.log(`ensureConnection:disconnect:${reason}`);
      this.disconnect();
      return;
    }

    const alreadyConnectedForSelection =
      this.activeMqClient?.active &&
      this.currentBrokerUrl === channel.activeMq.url &&
      this.currentEquipment === selectedEquipment;

    if (alreadyConnectedForSelection) {
      this.log(`ensureConnection:already-connected:${reason}`);
      return;
    }

    this.log(`ensureConnection:connect:${reason}`);
    this.connect(channel.activeMq, `Connecting (${reason}): ${selectedEquipment}`);
  }

  callback = (subscriptionConfig: AmqSubscriptionConfigI, message: any) => {
    const parsedItem = subscriptionConfig.map(JSON.parse(message.body));

    const action = subscriptionConfig.updateStore(parsedItem, this.currentPage);
    const dispatch = subscriptionConfig.checkBeforeDispatch(subscriptionConfig, parsedItem);

    if (dispatch && action) {
      this.appStore.dispatch(action);
    }
  }

  private reportDuplicateSubscriptions(channel: string): void {
    if (this.subscriptions.length <= 1 || this.duplicatedSubscriptionLogged) {
      return;
    }

    this.duplicatedSubscriptionLogged = true;
    this.log(`More than one active subscription detected for ${channel}.`, true);
  }

  private log(context: string, captureInSentry = false): void {
    const dataForLog = this.dataForLog();
    const message = `[AMQ] ${context} clientActive=${dataForLog.clientActive} clientConnected=${dataForLog.clientConnected} subscriptions=${dataForLog.numberOfSubscriptions} broker=${dataForLog.brokerUrl} equipment=${dataForLog.equipment} page=${dataForLog.page}`;
    
    console.info(message);
    this.appStore.dispatch(AmqSocketActions.log({
      logType: 'info',
      message,
    }));

    if (captureInSentry) {
      getCurrentScope()
        .setTag('amq_equipment', dataForLog.equipment ?? 'n/a')
        .setTag('amq_page', dataForLog.page ?? 'n/a')
        .setTag('amq_broker_url', dataForLog.brokerUrl ?? 'n/a')
        .setFingerprint([
          'amq-duplicate-subscription',
          dataForLog.brokerUrl,
          dataForLog.equipment,
        ]);
      captureMessage(
        `${context} count=${dataForLog.numberOfSubscriptions}, equipment=${dataForLog.equipment}, broker=${dataForLog.brokerUrl}, page=${dataForLog.page}`,
        { level: 'warning' }
      );
    }
  }

  private dataForLog(): {
    clientActive: boolean;
    clientConnected: boolean;
    numberOfSubscriptions: number;
    brokerUrl: string;
    equipment: string;
    page: string;
  } {
    return {
      clientActive: this.activeMqClient?.active ?? false,
      clientConnected: this.activeMqClient?.connected ?? false,
      numberOfSubscriptions: this.subscriptions.length,
      brokerUrl: this.currentBrokerUrl ?? 'n/a',
      equipment: this.currentEquipment ?? 'n/a',
      page: this.currentPage ?? 'n/a',
    }
  }
}
